// RegistrationScreen — Phase 1 Plan 05; telegram-ui migration Phase 12 Plan 12-02.
//
// react-hook-form + zod schema. Submits to POST /api/v1/companies через ApiClient.
// On success → navigate /trial?ends_at=<ISO>.
// On error → Banner.
//
// Презентация (Phase 12): single-column Section + Input/Select; optional
// VAT/phone в <Accordion> (progressive disclosure R6); primary CTA — нативная
// MainButton «Создать компанию» (useMainButton, эталон из ConsentScreen);
// BackButton → Consent; <Steps> (2/3). Бизнес-логика (zod Security V5 +
// POST companies + все data-testid) СОХРАНЕНА (UI-SPEC §10).

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { backButton } from "@telegram-apps/sdk-react";
import {
  Accordion,
  Banner,
  Button,
  Input,
  List,
  Section,
  Select,
  Steps,
} from "@telegram-apps/telegram-ui";

import { createCompany } from "../api/companies";
import { useAuth } from "../auth/useAuth";
import { haptic } from "../lib/haptics";
import { useMainButton } from "../lib/mainButton";
import { SAFE_AREA_BOTTOM } from "../lib/safeArea";

// EU 27 + TR + RS country codes (mirrors VAT_PATTERNS dict в backend).
const COUNTRY_CODES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "EL",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  "TR",
  "RS",
] as const;

const LOCALE_CODES = ["en", "tr", "bg"] as const;

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(200),
  // Zod 4: errorMap → error (Zod 4.x renamed; supports string или () => string).
  country: z.enum(COUNTRY_CODES, {
    error: () => "Select a supported country",
  }),
  vat_id: z.string().optional(),
  vat_country: z
    .string()
    .regex(/^[A-Z]{2}$/, "Invalid VAT country")
    .optional()
    .or(z.literal("")),
  contact_email: z.string().email("Invalid email"),
  locale: z.enum(LOCALE_CODES),
  phone: z.string().optional(),
});

// Per-country VAT regex (Phase 1 structural; matches validators/vat.py).
// Mirrors backend VAT_PATTERNS keys (29 entries).
const VAT_REGEX: Record<string, RegExp> = {
  AT: /^(AT)?U\d{8}$/,
  BE: /^(BE)?0?\d{9,10}$/,
  BG: /^(BG)?\d{9,10}$/,
  HR: /^(HR)?\d{11}$/,
  CY: /^(CY)?\d{8}[A-Z]$/,
  CZ: /^(CZ)?\d{8,10}$/,
  DK: /^(DK)?\d{8}$/,
  EE: /^(EE)?\d{9}$/,
  FI: /^(FI)?\d{8}$/,
  FR: /^(FR)?[A-HJ-NP-Z0-9]{2}\d{9}$/,
  DE: /^(DE)?\d{9}$/,
  EL: /^(EL)?\d{9}$/,
  HU: /^(HU)?\d{8}$/,
  IE: /^(IE)?\d[A-Z0-9+*]\d{5}[A-Z]{1,2}$/,
  IT: /^(IT)?\d{11}$/,
  LV: /^(LV)?\d{11}$/,
  LT: /^(LT)?(\d{9}|\d{12})$/,
  LU: /^(LU)?\d{8}$/,
  MT: /^(MT)?\d{8}$/,
  NL: /^(NL)?\d{9}B\d{2}$/,
  PL: /^(PL)?\d{10}$/,
  PT: /^(PT)?\d{9}$/,
  RO: /^(RO)?\d{2,10}$/,
  SK: /^(SK)?\d{10}$/,
  SI: /^(SI)?\d{8}$/,
  ES: /^(ES)?[A-Z0-9]\d{7}[A-Z0-9]$/,
  SE: /^(SE)?\d{12}$/,
  TR: /^\d{10}$/,
  RS: /^\d{9}$/,
};

export type RegistrationFormData = z.infer<typeof formSchema>;

interface RegistrationScreenProps {
  baseUrl?: string; // optional — uses apiClient with its own baseUrl
}

export function RegistrationScreen(_props: RegistrationScreenProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { apiClient } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [optionalOpen, setOptionalOpen] = useState<boolean>(false);

  // Native BackButton → Consent (один источник навигации назад). Вне Telegram —
  // no-op (SafeWrapped `.isAvailable()` guard). Cleanup on unmount (Pitfall 5).
  useEffect(() => {
    const back = backButton as unknown as {
      mount?: (() => void) & { isAvailable?: () => boolean };
      show?: (() => void) & { isAvailable?: () => boolean };
      hide?: (() => void) & { isAvailable?: () => boolean };
      onClick?: ((fn: () => void) => VoidFunction) & { isAvailable?: () => boolean };
    };
    const avail = (fn: unknown): boolean => {
      if (typeof fn !== "function") return false;
      const guard = (fn as { isAvailable?: unknown }).isAvailable;
      return typeof guard === "function" && (guard as () => boolean).call(fn);
    };

    if (avail(back.mount)) back.mount?.();
    if (avail(back.show)) back.show?.();
    let off: VoidFunction | undefined;
    if (avail(back.onClick)) {
      off = back.onClick?.(() => navigate("/"));
    }
    return () => {
      off?.();
      if (avail(back.hide)) back.hide?.();
    };
  }, [navigate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      locale: (i18n.language as "en" | "tr" | "bg") || "en",
    },
  });

  const watchedVat = watch("vat_id");
  const watchedVatCountry = watch("vat_country");
  const watchedCountry = watch("country");

  const vatStructuralError = ((): string | null => {
    if (!watchedVat || !watchedVat.trim()) return null;
    const country = (watchedVatCountry || watchedCountry || "").trim().toUpperCase();
    const pattern = VAT_REGEX[country];
    if (!pattern) {
      return t("errors.vat_country_unsupported", { country });
    }
    const cleaned = watchedVat.trim().toUpperCase().replace(/[\s-]/g, "");
    if (!pattern.test(cleaned)) {
      return t("errors.vat_invalid");
    }
    return null;
  })();

  const onSubmit = async (data: RegistrationFormData): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        name: data.name,
        country: data.country,
        contact_email: data.contact_email,
        locale: data.locale,
        vat_id: data.vat_id?.trim() || null,
        vat_country: data.vat_country?.trim() || data.country || null,
        phone: data.phone?.trim() || null,
      };
      const res = await createCompany(apiClient, payload);
      haptic.success();
      navigate(`/trial?ends_at=${encodeURIComponent(res.trial_ends_at)}`);
    } catch (err) {
      setSubmitError((err as Error).message);
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = isValid && !submitting && vatStructuralError === null;
  const triggerSubmit = handleSubmit(onSubmit);

  // Native Telegram MainButton «Создать компанию» — primary CTA (Pitfall 5).
  // onClick проходит через handleSubmit (zod re-validation). Вне Telegram —
  // fallback <Button> ниже с тем же data-testid.
  useMainButton({
    text: t("registration.create_company"),
    onClick: () => {
      void triggerSubmit();
    },
    isEnabled: canSubmit,
    isLoading: submitting,
  });

  return (
    <List>
      <Section header={<Steps count={3} progress={1} />}>
        <form onSubmit={triggerSubmit} data-testid="registration-form">
          <Input
            header={t("registration.company_name")}
            type="text"
            status={errors.name ? "error" : "default"}
            {...register("name")}
            data-testid="reg-name-input"
          />
          {errors.name && (
            <Banner
              type="section"
              description={errors.name.message}
              role="alert"
              data-testid="reg-name-error"
            />
          )}

          <Select
            header={t("registration.country")}
            status={errors.country ? "error" : "default"}
            {...register("country")}
            data-testid="reg-country-select"
          >
            <option value="">{t("registration.country_placeholder")}</option>
            {COUNTRY_CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {errors.country && (
            <Banner type="section" description={errors.country.message} role="alert" />
          )}

          <Input
            header={t("registration.contact_email")}
            type="email"
            status={errors.contact_email ? "error" : "default"}
            {...register("contact_email")}
            data-testid="reg-email-input"
          />
          {errors.contact_email && (
            <Banner type="section" description={errors.contact_email.message} role="alert" />
          )}

          {/* Optional fields — progressive disclosure (R6) via Accordion. */}
          <Accordion expanded={optionalOpen} onChange={setOptionalOpen} id="reg-optional-accordion">
            <Accordion.Summary data-testid="reg-optional-toggle">
              {t("registration.optional_details")}
            </Accordion.Summary>
            <Accordion.Content>
              <Input
                header={t("registration.vat_id")}
                type="text"
                status={vatStructuralError ? "error" : "default"}
                {...register("vat_id")}
                data-testid="reg-vat-input"
              />
              {vatStructuralError && (
                <Banner
                  type="section"
                  description={vatStructuralError}
                  role="alert"
                  data-testid="reg-vat-error"
                />
              )}
              <Input
                header={t("registration.phone")}
                type="tel"
                {...register("phone")}
                data-testid="reg-phone-input"
              />
            </Accordion.Content>
          </Accordion>

          {submitError && (
            <Banner
              type="section"
              description={submitError}
              role="alert"
              data-testid="reg-submit-error"
            />
          )}

          {/* Fallback CTA для окружений без нативной MainButton (dev / vitest jsdom).
              Тот же data-testid; внутри Telegram primary — нативная MainButton выше. */}
          <div style={{ paddingBottom: SAFE_AREA_BOTTOM }}>
            <Button
              type="submit"
              size="l"
              stretched
              disabled={!canSubmit}
              loading={submitting}
              data-testid="reg-submit-button"
            >
              {t("registration.create_company")}
            </Button>
          </div>
        </form>
      </Section>
    </List>
  );
}
